import React from 'react';
import PropTypes from 'prop-types';
import GLOBALS from '../config/globals';

// Renders a grid of earned badges followed by "???" placeholders for unearned ones,
// tier-matched. All display text for earned badges is pre-composed by the backend —
// the frontend is intentionally kept ignorant of badge catalogs, flavor text templates,
// and identifying info for unowned badges.
const TIER_NAMES = { 1: 'bronze', 2: 'silver', 3: 'gold', 4: 'diamond' };

const LockedBadge = ({ tier, index }) => {
    const tierName = TIER_NAMES[tier];
    const tierConfig = GLOBALS.BADGE_TIERS[tier] ?? GLOBALS.BADGE_TIERS[1];
    return (
        <div
            key={`locked-${tierName}-${index}`}
            className={`profile-badge ${tierConfig.cssClass} profile-badge--locked`}
            title="Keep playing to unlock"
        >
            <div className="profile-badge__icon">
                <img
                    src={`/img/badges/badge-question-${tierName}.png`}
                    alt="Locked badge"
                    className="profile-badge__img"
                    onError={e => { e.currentTarget.style.display = 'none'; }}
                />
            </div>
            <div className="profile-badge__info">
                <div className="profile-badge__name">???</div>
                <div className="profile-badge__sentence">???</div>
            </div>
        </div>
    );
};

LockedBadge.propTypes = {
    tier: PropTypes.number.isRequired,
    index: PropTypes.number.isRequired,
};

const EMPTY_LOCKED = { bronze: 0, silver: 0, gold: 0, diamond: 0 };

const BadgeCatalog = ({ badges, showLocked = false }) => {
    const earned = badges?.earned ?? [];
    const locked = { ...EMPTY_LOCKED, ...(badges?.locked ?? {}) };
    const totalLocked = locked.bronze + locked.silver + locked.gold + locked.diamond;

    if (earned.length === 0 && (!showLocked || totalLocked === 0)) return null;

    return (
        <div className="profile-badges">
            {earned.map(badge => {
                const tierConfig = GLOBALS.BADGE_TIERS[badge.tier] ?? GLOBALS.BADGE_TIERS[1];
                return (
                    <div
                        key={badge.badgeID}
                        className={`profile-badge ${tierConfig.cssClass} profile-badge--${badge.type}`}
                        title={badge.description}
                    >
                        <div className="profile-badge__icon">
                            <img
                                src={`/img/badges/${encodeURIComponent(badge.imageKey)}.png`}
                                alt={badge.name}
                                className="profile-badge__img"
                                onError={e => { e.currentTarget.style.display = 'none'; }}
                            />
                        </div>
                        <div className="profile-badge__info">
                            <div className="profile-badge__name">{badge.name}</div>
                            <div className="profile-badge__sentence">{badge.description}</div>
                        </div>
                    </div>
                );
            })}
            {showLocked && (
                <>
                    {Array.from({ length: locked.bronze }).map((_, i) => <LockedBadge key={`locked-bronze-${i}`} tier={1} index={i} />)}
                    {Array.from({ length: locked.silver }).map((_, i) => <LockedBadge key={`locked-silver-${i}`} tier={2} index={i} />)}
                    {Array.from({ length: locked.gold }).map((_, i) => <LockedBadge key={`locked-gold-${i}`} tier={3} index={i} />)}
                    {Array.from({ length: locked.diamond }).map((_, i) => <LockedBadge key={`locked-diamond-${i}`} tier={4} index={i} />)}
                </>
            )}
        </div>
    );
};

BadgeCatalog.propTypes = {
    badges: PropTypes.shape({
        earned: PropTypes.arrayOf(PropTypes.shape({
            badgeID: PropTypes.oneOfType([PropTypes.number, PropTypes.string]).isRequired,
            name: PropTypes.string.isRequired,
            type: PropTypes.string,
            tier: PropTypes.number,
            imageKey: PropTypes.string.isRequired,
            subject: PropTypes.string,
            description: PropTypes.string.isRequired,
        })),
        locked: PropTypes.shape({
            bronze: PropTypes.number,
            silver: PropTypes.number,
            gold: PropTypes.number,
            diamond: PropTypes.number,
        }),
    }),
    showLocked: PropTypes.bool,
};

export default BadgeCatalog;
